import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconBoxMultiple } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useOrgDetail } from '../api/dashboard';
import {
  useDeleteOrg,
  useMarkOrgSynthetic,
  useOrg,
  useOrgRetention,
  useRestoreOrg,
  useSetOrgRetention,
  useUpdateOrg,
} from '../api/orgs';
import { useMe } from '../api/session';
import { DegradedBanner } from '../components/degraded-banner';
import { DermConfigCard } from '../components/derm-config-card';
import { IsolationConfigCard } from '../components/isolation-config-card';
import { MetricCard } from '../components/metric-card';
import { OrgHierarchyCard } from '../components/org-hierarchy-card';
import { WebhookEndpointsCard } from '../components/webhook-endpoints-card';

export function OrgDetailPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();

  const dashboard = useOrgDetail(orgId);
  const orgQuery = useOrg(orgId);
  const updateOrg = useUpdateOrg(orgId);
  const deleteOrg = useDeleteOrg(orgId);
  const restoreOrg = useRestoreOrg(orgId);
  const retentionQuery = useOrgRetention(orgId);
  const setRetention = useSetOrgRetention(orgId);
  const meQuery = useMe();
  const markSynthetic = useMarkOrgSynthetic(orgId);
  const [confirmSynthetic, setConfirmSynthetic] = useState(false);
  const isSuperAdmin = meQuery.data?.role === 'super_admin';

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  // The retention policy loads asynchronously, so seed the NumberInput from
  // the query data once it arrives. `loadedRetention` is the persisted value
  // we diff against to enable/disable Save.
  const [retentionYears, setRetentionYears] = useState<number | null>(null);
  const [loadedRetention, setLoadedRetention] = useState<number | null>(null);
  const [retentionError, setRetentionError] = useState<string | null>(null);

  useEffect(() => {
    if (orgQuery.data) {
      setName(orgQuery.data.name);
    }
  }, [orgQuery.data]);

  useEffect(() => {
    if (retentionQuery.data) {
      setRetentionYears(retentionQuery.data.retention_years);
      setLoadedRetention(retentionQuery.data.retention_years);
    }
  }, [retentionQuery.data]);

  if (orgQuery.isLoading) return <Text p="md">Loading…</Text>;
  if (orgQuery.isError || !orgQuery.data) return <Text p="md">Organisation not found.</Text>;

  const org = orgQuery.data;
  const archived = Boolean(org.deletedAt);
  const heading = org.name || orgId;

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    if (trimmed.length > 255) {
      setError('Name must be 255 characters or fewer.');
      return;
    }
    updateOrg.mutate(
      { name: trimmed },
      {
        onError: (err) => setError(formatApiError(err, 'Failed to update.')),
      },
    );
  };

  const handleArchive = () => {
    deleteOrg.mutate(undefined, {
      onSuccess: () => {
        setConfirmArchive(false);
        orgQuery.refetch();
      },
      onError: (err) => {
        setConfirmArchive(false);
        setError(formatApiError(err, 'Failed to archive.'));
      },
    });
  };

  const handleRestore = () => {
    restoreOrg.mutate(undefined, {
      onSuccess: () => orgQuery.refetch(),
      onError: (err) => setError(formatApiError(err, 'Failed to restore.')),
    });
  };

  const handleSaveRetention = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRetentionError(null);
    if (retentionYears === null || retentionYears < 1) {
      setRetentionError('Enter a retention period of at least 1 year.');
      return;
    }
    setRetention.mutate(retentionYears, {
      onError: (err) =>
        setRetentionError(formatApiError(err, 'Failed to update retention period.')),
    });
  };

  const retentionUnchanged = retentionYears === loadedRetention;
  const retentionInvalid = retentionYears === null || retentionYears < 1;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Group>
          <Title order={2}>{heading}</Title>
          <Badge variant="light">{org.region.toUpperCase()}</Badge>
          {org.synthetic && (
            <Badge color="grape" variant="light">
              Synthetic
            </Badge>
          )}
          {archived && (
            <Badge color="gray" variant="light">
              archived
            </Badge>
          )}
        </Group>
        <Group>
          <Button variant="subtle" component={Link} to="/orgs">
            ← back to organisations
          </Button>
          <Button
            component={Link}
            to={`/orgs/${orgId}/products`}
            variant="default"
            leftSection={<IconBoxMultiple size={16} />}
          >
            Manage products
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      {dashboard.data && <DegradedBanner degradedFor={dashboard.data.degradedFor} />}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        <MetricCard
          title="Cases (this week)"
          isLoading={dashboard.isLoading}
          isError={
            dashboard.isError ||
            (dashboard.data?.volume === null && dashboard.data?.partial === true)
          }
          partial={dashboard.data?.degradedFor.includes('clinical-api')}
        >
          <Text fw={700}>{dashboard.data?.volume?.cases.inRange ?? '—'}</Text>
        </MetricCard>
        <MetricCard
          title="AI success rate (last 7 days)"
          isLoading={dashboard.isLoading}
          isError={
            dashboard.isError ||
            (dashboard.data?.aiReview === null && dashboard.data?.partial === true)
          }
          partial={dashboard.data?.degradedFor.includes('ai-review')}
        >
          <Text fw={700}>
            {(() => {
              // Success rate sourced from ai-review's successRateInRange —
              // the authoritative inference record after derm_review was dropped.
              const rate = dashboard.data?.aiReview?.inferences.successRateInRange;
              if (rate === undefined || rate === null) return '—';
              return `${(rate * 100).toFixed(1)}%`;
            })()}
          </Text>
          <Text size="xs" c="dimmed">
            {dashboard.data?.aiReview?.inferences.successInRange ?? 0} succeeded,{' '}
            {dashboard.data?.aiReview?.inferences.failureInRange ?? 0} failed
          </Text>
        </MetricCard>
        <MetricCard
          title="Human review queue"
          isLoading={dashboard.isLoading}
          isError={
            dashboard.isError ||
            (dashboard.data?.humanReview === null && dashboard.data?.partial === true)
          }
          partial={dashboard.data?.degradedFor.includes('human-review')}
        >
          <Text fw={700}>{dashboard.data?.humanReview?.queue.open ?? '—'}</Text>
          <Text size="xs" c="dimmed">
            open
          </Text>
        </MetricCard>
      </SimpleGrid>

      <Card withBorder padding="lg" maw={680}>
        <Stack gap="sm">
          <Title order={5}>Details</Title>
          <Box component="form" onSubmit={handleSave} noValidate>
            <Stack gap="md">
              <TextInput
                label="Name"
                required
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                disabled={updateOrg.isPending || archived}
              />
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  Region:
                </Text>
                <Badge variant="light">{org.region.toUpperCase()}</Badge>
              </Group>
              <Group justify="flex-end">
                <Button
                  type="submit"
                  loading={updateOrg.isPending}
                  disabled={archived || name.trim() === org.name}
                >
                  Save
                </Button>
              </Group>
            </Stack>
          </Box>
        </Stack>
      </Card>

      <Card withBorder padding="lg" maw={680}>
        <Stack gap="sm">
          <Title order={5}>Data retention</Title>
          <Box component="form" onSubmit={handleSaveRetention} noValidate>
            <Stack gap="md">
              <NumberInput
                label="Patient data retention (years)"
                description={
                  loadedRetention === null
                    ? 'No retention period set. Records cannot be deleted within this many years without an override + reason.'
                    : 'Records cannot be deleted within this many years without an override + reason.'
                }
                min={1}
                value={retentionYears ?? ''}
                onChange={(value) => setRetentionYears(typeof value === 'number' ? value : null)}
                disabled={setRetention.isPending || archived}
              />
              {retentionError && (
                <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                  {retentionError}
                </Alert>
              )}
              {setRetention.isSuccess && !retentionError && (
                <Text size="sm" c="green">
                  Saved.
                </Text>
              )}
              <Group justify="flex-end">
                <Button
                  type="submit"
                  loading={setRetention.isPending}
                  disabled={archived || retentionInvalid || retentionUnchanged}
                >
                  Save
                </Button>
              </Group>
            </Stack>
          </Box>
        </Stack>
      </Card>

      <DermConfigCard orgId={orgId} archived={archived} />

      <IsolationConfigCard org={org} />
      <OrgHierarchyCard org={org} />

      <WebhookEndpointsCard orgId={orgId} />

      {isSuperAdmin && !org.synthetic && (
        <Card withBorder padding="lg" maw={680}>
          <Stack gap="sm">
            <Title order={5}>Simulator</Title>
            <Text size="sm" c="dimmed">
              Synthetic orgs are reserved for the platform simulator. Once marked synthetic, this
              cannot be undone. Synthetic orgs may be subject to bulk data purge.
            </Text>
            <Group>
              <Tooltip
                label="Cannot mark as synthetic — this org has existing cases. Synthetic orgs are subject to bulk data purge."
                disabled={!org.caseCount || org.caseCount === 0}
              >
                <span>
                  <Button
                    variant="default"
                    disabled={
                      Boolean(org.caseCount && org.caseCount > 0) || markSynthetic.isPending
                    }
                    onClick={() => setConfirmSynthetic(true)}
                  >
                    Mark as Synthetic
                  </Button>
                </span>
              </Tooltip>
            </Group>
          </Stack>
        </Card>
      )}

      <Card withBorder padding="lg" maw={680}>
        <Stack gap="sm">
          <Title order={5}>{archived ? 'Restore' : 'Danger zone'}</Title>
          {archived ? (
            <>
              <Text size="sm" c="dimmed">
                This organisation is archived and not visible in the normal list.
              </Text>
              <Group>
                <Button variant="default" onClick={handleRestore} loading={restoreOrg.isPending}>
                  Restore organisation
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text size="sm" c="dimmed">
                Archiving hides this organisation from the active list. You can restore it later.
                Cannot archive while patients still belong to this organisation.
              </Text>
              <Group>
                <Button color="red" variant="subtle" onClick={() => setConfirmArchive(true)}>
                  Archive organisation
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Card>

      <Modal
        opened={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        title="Archive this organisation?"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Text size="sm">
            The organisation will be hidden from the default list. This is reversible.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setConfirmArchive(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleArchive} loading={deleteOrg.isPending}>
              Confirm archive
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmSynthetic}
        onClose={() => setConfirmSynthetic(false)}
        title="Make this org synthetic?"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Text size="sm">
            This is a one-way operation — once marked synthetic, this org cannot be converted back.
            Synthetic orgs are reserved for the platform simulator and may be subject to bulk data
            purge.
          </Text>
          {markSynthetic.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {formatApiError(markSynthetic.error, 'Failed to mark as synthetic.')}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setConfirmSynthetic(false)}>
              Cancel
            </Button>
            <Button
              color="grape"
              onClick={() => {
                markSynthetic.mutate(undefined, {
                  onSuccess: () => setConfirmSynthetic(false),
                });
              }}
              loading={markSynthetic.isPending}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function formatApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      try {
        const parsed = JSON.parse(err.body) as { patientCount?: number; message?: string };
        if (parsed.patientCount !== undefined) {
          return `Cannot archive — ${parsed.patientCount} patients still belong to this organisation.`;
        }
        if (parsed.message) return parsed.message;
      } catch {
        /* fallthrough */
      }
      return 'Cannot archive — patients still belong to this organisation.';
    }
    if (err.status === 400) return 'Validation failed. Check the highlighted fields.';
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 403) return 'Your account does not have permission for this action.';
    if (err.status === 404) return 'Organisation not found.';
  }
  return fallback;
}
